import React, { useEffect, useState } from 'react';
import { Box, Button, Modal, Typography, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { dashboardViewSelector, isUpdatingProfile } from '../../pages/Dashboard/selectors';
import { updateUserProfile } from '../../redux/user/actions';
import { useDispatchPromise } from '../../utils/AppUtils';
import { catchAsync } from '../../utils/AppUtils';
import type { TDashboardView } from '../../utils/dashboardView';
import { navigateToDashboardForView, normalizeDashboardView } from '../../utils/dashboardView';

const OPTIONS: {
    id: TDashboardView;
    title: string;
    subtitle: string;
    Icon: React.ElementType;
}[] = [
    {
        id: 'brand',
        title: 'Brand Dashboard',
        subtitle: 'Campaigns & analytics',
        Icon: StorefrontOutlinedIcon,
    },
    {
        id: 'creator',
        title: 'Creator Dashboard',
        subtitle: 'Content & earnings',
        Icon: AutoAwesomeIcon,
    },
];

/** Circular swap icon: ring with two horizontal arrows (↔) — matches reference artwork. */
const SwitchViewGlyph: React.FC<{ size?: number }> = ({ size = 20 }) => (
    <Box
        component="svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        sx={{ flexShrink: 0, display: 'block', color: 'currentColor' }}>
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
        {/* Upper: arrow pointing right */}
        <path
            d="M7 9h6M13 9l2.2-2M13 9l2.2 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        {/* Lower: arrow pointing left */}
        <path
            d="M17 15h-6M11 15l-2.2-2M11 15l-2.2 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </Box>
);

export const DashboardViewTrigger: React.FC<{ onOpen: () => void; compact?: boolean }> = ({
    onOpen,
    compact = false,
}) => {
    const { themeStyle, device } = useThemeContext();
    const isMobileLayout = device.isMobile;
    const iconOnly = isMobileLayout || compact;

    return (
        <Box
            component="button"
            type="button"
            id="switch-view-btn"
            data-skip-brand-profile-gate="true"
            onClick={onOpen}
            aria-label="Switch dashboard view"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: iconOnly ? 0 : 1,
                borderRadius: 999,
                px: compact ? 0.5 : isMobileLayout ? 0.85 : 1.75,
                py: compact ? 0.5 : 0.75,
                minHeight: compact ? 32 : 36,
                minWidth: iconOnly ? (compact ? 32 : 36) : 'auto',
                flexShrink: 0,
                border: `1px solid ${theme[themeStyle].placeholderGray}`,
                backgroundColor: theme[themeStyle].pure_black,
                color: theme[themeStyle].form_label,
                cursor: 'pointer',
                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                fontSize: 12,
                '&:hover': {
                    borderColor: theme[themeStyle].primary_orange,
                    color: theme[themeStyle].primary_orange,
                },
            }}>
            <SwitchViewGlyph size={compact ? 18 : 20} />
            {!iconOnly && (
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        color: 'inherit',
                        lineHeight: 1,
                        textTransform: 'none',
                    }}>
                    Switch
                </Typography>
            )}
        </Box>
    );
};

export const DashboardViewOverlay: React.FC<{
    open: boolean;
    onClose: () => void;
}> = ({ open, onClose }) => {
    const { themeStyle } = useThemeContext();
    const mui = useMuiTheme();
    const isNarrow = useMediaQuery(mui.breakpoints.down('md'));
    const currentFromStore = useSelector(dashboardViewSelector);
    const updating = useSelector(isUpdatingProfile);
    const dispatchPromise = useDispatchPromise();
    const navigate = useNavigate();
    const [selected, setSelected] = useState<TDashboardView>(currentFromStore);

    useEffect(() => {
        if (open) {
            setSelected(normalizeDashboardView(currentFromStore));
        }
    }, [open, currentFromStore]);

    const handleConfirm = catchAsync(async () => {
        await dispatchPromise(updateUserProfile({ dashboard_view: selected }));
        onClose();
        navigateToDashboardForView(navigate, selected);
    });

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="dashboard-view-switch-title"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.82)',
                        backdropFilter: 'blur(10px)',
                    },
                },
            }}
            sx={{ zIndex: 10050 }}>
            <Box
                data-skip-brand-profile-gate="true"
                sx={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: isNarrow ? 'flex-end' : 'center',
                    justifyContent: 'center',
                    outline: 'none',
                    p: isNarrow ? 0 : 2,
                }}>
                <Box
                    data-skip-brand-profile-gate="true"
                    sx={{
                        width: '100%',
                        maxWidth: 520,
                        bgcolor: theme[themeStyle].pure_black,
                        border: `1px solid ${theme[themeStyle].placeholderGray}`,
                        borderRadius: isNarrow ? '24px 24px 0 0' : 4,
                        boxShadow: '0 40px 100px rgba(0,0,0,0.65)',
                        p: { xs: 3, sm: 4 },
                        maxHeight: isNarrow ? '90vh' : 'none',
                        overflowY: 'auto',
                    }}
                    onClick={e => e.stopPropagation()}>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography
                            id="dashboard-view-switch-title"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 22, sm: 26 },
                                color: theme[themeStyle].primary_white,
                                mb: 0.5,
                            }}>
                            Switch account view
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                color: theme[themeStyle].form_label,
                            }}>
                            Select your primary workspace to continue.
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                        {OPTIONS.map(({ id, title, subtitle, Icon }) => {
                            const active = selected === id;
                            return (
                                <Box
                                    key={id}
                                    component="button"
                                    type="button"
                                    onClick={() => setSelected(id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        width: '100%',
                                        p: 2,
                                        borderRadius: 999,
                                        border: active
                                            ? `2px solid ${theme[themeStyle].primary_orange}`
                                            : `1px solid transparent`,
                                        bgcolor: active
                                            ? theme[themeStyle].zeus_dark ?? 'rgba(255,255,255,0.06)'
                                            : theme[themeStyle].prisma_dark ?? 'rgba(255,255,255,0.04)',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'border-color 0.2s, background 0.2s',
                                        '&:hover': {
                                            bgcolor: theme[themeStyle].zeus_dark ?? 'rgba(255,255,255,0.08)',
                                        },
                                    }}>
                                    <Box
                                        sx={{
                                            width: 52,
                                            height: 52,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: active
                                                ? theme[themeStyle].primary_orange
                                                : theme[themeStyle].darkGray60 ?? 'rgba(255,255,255,0.08)',
                                            color: active ? theme[themeStyle].pure_black : theme[themeStyle].form_label,
                                            flexShrink: 0,
                                        }}>
                                        <Icon sx={{ fontSize: 28 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                fontSize: 16,
                                                color: active
                                                    ? theme[themeStyle].primary_orange
                                                    : theme[themeStyle].true_white,
                                            }}>
                                            {title}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: 11,
                                                color: theme[themeStyle].form_label,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.6,
                                            }}>
                                            {subtitle}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 26,
                                            height: 26,
                                            borderRadius: '50%',
                                            border: active ? 'none' : `2px solid ${theme[themeStyle].placeholderGray}`,
                                            bgcolor: active ? theme[themeStyle].primary_orange : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                        {active && (
                                            <CheckIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme[themeStyle].pure_black,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            disabled={updating}
                            onClick={handleConfirm}
                            sx={{
                                py: 1.75,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 16,
                                textTransform: 'none',
                                bgcolor: theme[themeStyle].primary_orange,
                                color: theme[themeStyle].pure_black,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: theme[themeStyle].primary_orange,
                                },
                            }}>
                            {updating ? 'Saving…' : 'Confirm'}
                        </Button>
                        <Button
                            fullWidth
                            onClick={onClose}
                            disabled={updating}
                            sx={{
                                py: 1.25,
                                borderRadius: 999,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                fontSize: 13,
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                color: theme[themeStyle].form_label,
                            }}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
};
